import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Check, AlertTriangle } from "lucide-react";
import { useEmailSubscription } from "@/hooks/use-email-subscription";

const EmailSubscription = () => {
  const { email, name, state, errorMessage, setEmail, setName, subscribe } =
    useEmailSubscription();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    subscribe();
  };

  return (
    <section
      id="join-community"
      className="py-16 md:py-20 navy-gradient relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

      <div className="container mx-auto px-4 md:px-6 relative w-full max-w-full">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-6 animate-fade-in opacity-0">
            <Mail className="h-8 w-8 text-gold" />
          </div>

          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4 animate-fade-in opacity-0"
            style={{ animationDelay: "0.1s" }}
          >
            Join 10,000+ Smart Women
          </h2>

          <p
            className="text-white/90 text-lg mb-8 animate-fade-in opacity-0"
            style={{ animationDelay: "0.2s" }}
          >
            Get exclusive tips, product recommendations, and special deals
            delivered straight to your inbox weekly.
          </p>

          <form
            onSubmit={handleSubmit}
            className="space-y-4 animate-fade-in opacity-0"
            style={{ animationDelay: "0.3s" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Your name"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-gold/50"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                aria-label="Your name"
                disabled={state === "submitting" || state === "success"}
              />
              <Input
                type="email"
                placeholder="Your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-gold/50"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-label="Your email address"
                disabled={state === "submitting" || state === "success"}
              />
            </div>

            {errorMessage && (
              <div className="flex items-center text-red-300 text-sm bg-red-900/20 p-2 rounded">
                <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gold hover:bg-gold-dark text-navy font-medium transition-all duration-300"
              disabled={state === "submitting" || state === "success"}
              aria-label="Subscribe to our newsletter"
            >
              {state === "submitting" ? (
                <>
                  <span className="animate-pulse">Processing...</span>
                </>
              ) : state === "success" ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  <span>Subscribed!</span>
                </>
              ) : (
                "Subscribe Now"
              )}
            </Button>
          </form>

          <p
            className="text-white/70 text-xs mt-4 animate-fade-in opacity-0"
            style={{ animationDelay: "0.4s" }}
          >
            By subscribing, you agree to our Privacy Policy and consent to
            receive updates from GlowHer.Life
          </p>
        </div>
      </div>
    </section>
  );
};

export default EmailSubscription;
